import React, { useEffect, useState } from "react";
import { sendVerificationCode, ResendVerificationCode } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";

export default function VerifyForm() {
  const [form, setForm] = useState({
    identifier: "",
    code: "",
  });

  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { verify: setUser } = useAuth();

  useEffect(() => {
    const identifier = localStorage.getItem("identifier");
    if (identifier) {
      setForm((prev) => ({ ...prev, identifier }));
    } else {
      toast.error("Missing identifier. Please register again.");
    }
  }, []);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await sendVerificationCode({ email: form.identifier, code: form.code });
      localStorage.removeItem("identifier");
      toast.success("Verification successful! Redirecting...");
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Verification failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    try {
      await ResendVerificationCode({ email: form.identifier });
      toast.success("A new verification code has been sent to your email.");
      setResendCooldown(60);  // 60 seconds cooldown
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Failed to resend code. Please try again."
      );
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <form onSubmit={handleVerify} className="space-y-4">
      <input
        type="text"
        name="identifier"
        className="w-full border border-gray-300 bg-gray-100 p-3 rounded-md text-sm text-gray-600"
        value={form.identifier}
        readOnly
      />

      <input
        type="text"
        name="code"
        placeholder="Enter verification code"
        className="w-full border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 p-3 rounded-md text-sm"
        value={form.code}
        onChange={handleChange}
        required
      />

      <button
        type="submit"
        className={`w-full py-3 rounded-md text-white font-semibold text-lg transition duration-200 ${
          loading
            ? "bg-blue-400 cursor-not-allowed"
            : "bg-blue-600 hover:bg-blue-700"
        }`}
        disabled={loading}
      >
        {loading ? "Verifying..." : "Verify"}
      </button>

      <button
        type="button"
        className={`w-full py-3 rounded-md text-blue-600 border border-blue-600 font-semibold text-lg transition duration-200 ${
          resendLoading || resendCooldown > 0
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-blue-50"
        }`}
        onClick={handleResendCode}
        disabled={resendLoading || resendCooldown > 0}
      >
        {resendLoading
          ? "Resending..."
          : resendCooldown > 0
          ? `Resend Code (${resendCooldown}s)`
          : "Resend Code"}
      </button>
    </form>
  );
}
